/**
 * ============================================================================
 * CHAT AGENT API TYPES — mirrors the GenerateEmbedUrlForChatAgent Smithy shapes.
 * ============================================================================
 *
 * Source of truth: HILTBusinessReportingServiceModel
 * model/requests|responses/generateEmbedUrlForChatAgent*.smithy (CR-299320420).
 * The service is an authorizing proxy to the ADSInsights QuickSight embed API:
 * the caller names the agent (a closed enum) and the alias the embed is for;
 * Midway callers are overridden server-side to their authenticated alias, so
 * the client-sent alias is a hint, never trusted.
 */

/** Wire id of the CHAT_AGENT feature in the GetPermissions profile (visibility gate). */
export const CHAT_AGENT_FEATURE_ID = 'chat_agent';

/**
 * The agents a caller may request a chat embed for. A closed enum on the
 * service (model `AgentName`): the UI passes the stable name, never a raw
 * QuickSight id, and the service maps it to the per-stage quick id.
 */
export type AgentName = 'wbr_agent';

/** The WBR (Weekly Business Review) chat agent — the only agent today. */
export const WBR_AGENT: AgentName = 'wbr_agent';

export interface GenerateEmbedUrlForChatAgentInput {
  /** The agent to open a chat embed for. Callers pass the name, not the QuickSight id. */
  agentName: AgentName;
  /**
   * The Amazon alias the embed is issued for. For a Midway caller a differing
   * value is overridden server-side with the authenticated alias.
   */
  userAlias: string;
}

export interface GenerateEmbedUrlForChatAgentOutput {
  /**
   * The signed QuickSight embed URL the UI renders in an iframe. Effectively a
   * bearer credential: user-specific, non-transferable, and time-limited
   * (roughly five minutes to open, a ten-hour session once opened). Treat as
   * sensitive — never log it.
   */
  chatEmbedUrl: string;
  /** The user the embed was issued for, as echoed by the embed API (e.g. "jdoe@amazon.com"). */
  user: string;
  /** The per-stage QuickSight agent quick id the requested agent name resolved to. */
  agentQuickId: string;
}
